import { createContext, useState, useEffect, useContext, useCallback } from "react";
import { useMsal } from "@azure/msal-react";
import { InteractionRequiredAuthError } from "@azure/msal-browser";
import axios from "axios";
import { loginRequest } from "../auth/authConfig";
import config from "../config";
import { installAuthenticatedFetch, setAccessTokenProvider, uninstallAuthenticatedFetch } from "../api/httpClient";
import { getCurrentUser, getCurrentUserPrivileges } from "../api/userApi";
import { DEV_IMPERSONATION_HEADER, getDevImpersonationEmail } from "../auth/devImpersonation";

const AuthContext = createContext();
const RETURN_URL_STORAGE_KEY = "returnUrl";

const currentInternalUrl = () => `${window.location.pathname}${window.location.search}${window.location.hash}`;

export const AuthProvider = ({ children }) => {
    const { accounts, instance } = useMsal();
    const [loggedIn, setLoggedIn] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const [token, setToken_] = useState(null);
    const [currentUser, setCurrentUser] = useState(null);
    const [privileges, setPrivileges] = useState([]);
    const [authorizationLoaded, setAuthorizationLoaded] = useState(false);

    useEffect(() => {
        if(accounts.length > 0){
            setLoggedIn(true);
            if (!instance.getActiveAccount()) {
                instance.setActiveAccount(accounts[0]);
            }
        }else {
            setLoggedIn(false);
            setToken_(null);
            setCurrentUser(null);
            setPrivileges([]);
            setAuthorizationLoaded(false);
            delete axios.defaults.headers.common["Authorization"];
        }
    }, [accounts, instance]);

    const login = async () => {
        setIsLoading(true);
        try {
            const returnUrl = currentInternalUrl();
            if (returnUrl.startsWith('/') && !returnUrl.startsWith('//')) {
                sessionStorage.setItem(RETURN_URL_STORAGE_KEY, returnUrl);
            }
            await instance.loginRedirect(loginRequest);
        } catch (error) {
            console.error("Login failed:", error);
        } finally {
            setIsLoading(false);
        }
    };

    const logout = async () => {
        try {
            setLoggedIn(false);
            setToken_(null);
            setCurrentUser(null);
            setPrivileges([]);
            setAuthorizationLoaded(false);
            delete axios.defaults.headers.common["Authorization"];
            await instance.logoutRedirect();
        } catch (error) {
            console.error("Logout failed:", error);
        }
    };

    const getToken = useCallback(async () => {
        const account = instance.getActiveAccount() || accounts[0] || instance.getAllAccounts()[0];

        if (!account) {
            return null;
        }

        try {
            const tokenResponse = await instance.acquireTokenSilent({
                ...loginRequest,
                account,
            });

            setToken_(tokenResponse.accessToken);
            axios.defaults.headers.common["Authorization"] = "Bearer " + tokenResponse.accessToken;
            return tokenResponse;
        }catch (error){
            if (error instanceof InteractionRequiredAuthError) {
                await instance.acquireTokenRedirect(loginRequest);
                return null;
            }

            console.error("Token acquisition failed:", error);
            throw error;
        }
    }, [accounts, instance]);

    useEffect(() => {
        setAccessTokenProvider(async () => {
            const tokenResponse = await getToken();

            return tokenResponse?.accessToken || null;
        });
        installAuthenticatedFetch();

        const requestInterceptorId = axios.interceptors.request.use(async (request) => {
            const url = request.url || '';
            const isBackendRequest = url.startsWith(config.baseResource) || url.startsWith('/');

            if (!isBackendRequest) {
                return request;
            }

            const devImpersonationEmail = getDevImpersonationEmail();

            if (devImpersonationEmail && !request.headers?.[DEV_IMPERSONATION_HEADER]) {
                request.headers = request.headers || {};
                request.headers[DEV_IMPERSONATION_HEADER] = devImpersonationEmail;
            }

            if (request.headers?.Authorization) {
                return request;
            }

            const tokenResponse = await getToken();

            if (tokenResponse?.accessToken) {
                request.headers = request.headers || {};
                request.headers.Authorization = `Bearer ${tokenResponse.accessToken}`;
            }

            return request;
        });

        return () => {
            setAccessTokenProvider(null);
            uninstallAuthenticatedFetch();
            axios.interceptors.request.eject(requestInterceptorId);
        };
    }, [getToken]);

    const refreshAuthorization = useCallback(async () => {
        const tokenResponse = await getToken();

        if (!tokenResponse?.accessToken) {
            return;
        }

        const authOptions = {token: tokenResponse.accessToken};
        const [userResponse, privilegesResponse] = await Promise.all([
            getCurrentUser(authOptions),
            getCurrentUserPrivileges(authOptions)
        ]);

        setCurrentUser(userResponse);
        setPrivileges(privilegesResponse || []);
        setAuthorizationLoaded(true);
    }, [getToken]);

    useEffect(() => {
        if (accounts.length === 0) {
            return;
        }

        refreshAuthorization().catch((error) => {
            console.error("Failed to load user authorization:", error);
            setCurrentUser(null);
            setPrivileges([]);
            setAuthorizationLoaded(true);
        });
    }, [accounts, refreshAuthorization]);

    return (
        <AuthContext.Provider value={{
            loggedIn,
            setLoggedIn,
            login,
            logout,
            getToken,
            accounts,
            token,
            setToken_,
            isLoading,
            currentUser,
            privileges,
            authorizationLoaded,
            refreshAuthorization
        }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => useContext(AuthContext);
